import { useRef, useEffect, useState } from "react";
import { Paper, Typography, Box } from "@mui/material";
import { Add } from "@mui/icons-material";
import SubProgramCard from "../SubProgramCard";
import { CurrencyOption, SubProgram } from "../../../types/program";
import styles from "./index.module.scss";
import { useFieldArray, Controller } from "react-hook-form";
import { Button } from "../../../common/components/Button";
import DeleteSubProgramDialog from "../DeleteSubProgramDialog";

interface SubProgramsSectionProps {
  control: any;
  setValue: (name: string, value: any) => void;
  watch: any;
  errors: any;
  getValues: any;
  programType: any;
  modeOfProgram?: string;
  startDate: Date | null;
  endDate: Date | null;
  venueOptions: string[];
  currencyOptions: any[];
  isDateWithinProgramRange: (date: Date) => boolean;
  subPrograms: any[];
  onAddSubProgram: () => void;
  onSubProgramChange: (id: string, field: string, value: any) => void;
  onSubProgramVenueChange: (id: string, value: string[]) => void;
  onDeleteSubProgram: (id: string) => void;
  subProgramDateRanges: { [key: string]: [Date | null, Date | null] };
  onSubProgramDateRangeChange: (
    id: string,
    startDate: Date | null,
    endDate: Date | null,
  ) => void;
  getSubProgramDateRange: (id: string) => [Date | null, Date | null];
  setSubProgramDateRange: (
    id: string,
    dateRange: [Date | null, Date | null],
  ) => void;
  existingSubPrograms?: any[];
  isEditMode?: boolean;
  trigger: any;
  onSubProgramBannerUpload?: (subProgramId: string, imageUrl: string) => void;
  noOfSession?: number;
  isGroupProgram?: boolean; // Optional prop to indicate if it's a group program
  programTypeNameFromSource?: string; // Optional prop to get program type name from source
}

const SubProgramsSection = ({
  control,
  setValue,
  watch,
  errors,
  getValues,
  programType,
  modeOfProgram = "online",
  startDate,
  endDate,
  venueOptions,
  currencyOptions,
  isDateWithinProgramRange,
  onDeleteSubProgram,
  subProgramDateRanges,
  onSubProgramDateRangeChange,
  getSubProgramDateRange,
  setSubProgramDateRange,
  existingSubPrograms = [],
  isEditMode = false,
  trigger,
  onSubProgramBannerUpload,
  noOfSession,
  isGroupProgram,
  programTypeNameFromSource,
}: SubProgramsSectionProps) => {
  const subProgramRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const { fields, append, remove } = useFieldArray({
    control,
    name: "subPrograms",
  });
  // Remove the conflicting initialization logic
  // Let the parent component handle all initialization via reset()

  useEffect(() => {
    if (noOfSession && fields.length === 0) {
      const parentMode = watch("modeOfProgram") || modeOfProgram;
      const parentCurrency = watch("currency") || "INR";
      const parentPaymentRequired = watch("isPaymentRequired") || "no";

      const newSubProgram = {
        title: "",
        description: "",
        startDate: null,
        endDate: null,
        modeOfProgram: parentMode,
        venueAddress: [],
        hasSeatLimit: programType?.maxCapacity > 0 ? "yes" : "no",
        seatLimit: programType?.maxCapacity?.toString() || "",
        hasWaitlist: programType?.waitlistApplicable ? "yes" : "no",
        waitlistTriggerCount:
          programType?.waitlistTriggerCount?.toString() || "",
        isPaymentRequired: parentPaymentRequired,
        currency: parentCurrency,
        programFee: watch("programFee") || "",
        isActive: true,
      };

      for (let i = 0; i < noOfSession; i++) {
        append({ ...newSubProgram });
      }
    }
  }, [noOfSession, append, fields.length, programType, watch]);

  const handleAddSubProgram = () => {
    const parentMode = watch("modeOfProgram") || modeOfProgram;
    const parentCurrency = watch("currency") || "INR";
    const parentPaymentRequired = watch("isPaymentRequired") || "no";

    const newSubProgram = {
      title: "",
      description: "",
      startDate: null,
      endDate: null,
      modeOfProgram: parentMode,
      venueAddress: [],
      hasSeatLimit: programType?.maxCapacity > 0 ? "yes" : "no",
      seatLimit: programType?.maxCapacity?.toString() || "",
      hasWaitlist: programType?.waitlistApplicable ? "yes" : "no",
      waitlistTriggerCount: programType?.waitlistTriggerCount?.toString() || "",
      isPaymentRequired: parentPaymentRequired,
      currency: parentCurrency,
      programFee: watch("programFee") || "",
      isActive: true,
    };

    const count = noOfSession || 1;

    for (let i = 0; i < count; i++) {
      append({ ...newSubProgram });
    }
  };

  // const handleRemoveSubProgram = (index: number) => {
  //   remove(index);

  //   // Call parent handler if provided
  //   if (onDeleteSubProgram) {
  //     onDeleteSubProgram(index.toString());
  //   }
  // };
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // underscore for private field
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null); // underscore for private field
  const handleRemoveSubProgram = (index: number) => {
    setDeleteIndex(index);
    setDeleteDialogOpen(true);
  };
  const handleConfirmDelete = () => {
    if (deleteIndex !== null) {
      remove(deleteIndex);
      if (onDeleteSubProgram) {
        onDeleteSubProgram(deleteIndex.toString());
      }
    }
    setDeleteDialogOpen(false);
    setDeleteIndex(null);
  };

  // Cancel delete: just close dialog
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteIndex(null);
  };

  const handleSubProgramDateChange = (index: number) => {
    return (startDate: Date | null, endDate: Date | null) => {
      setValue(`subPrograms.${index}.startDate`, startDate);
      setValue(`subPrograms.${index}.endDate`, endDate);

      // Call parent handler if provided
      if (onSubProgramDateRangeChange) {
        onSubProgramDateRangeChange(index.toString(), startDate, endDate);
      }
    };
  };

  const handleSubProgramFieldChange = (
    index: number,
    field: string,
    value: any,
  ) => {
    setValue(`subPrograms.${index}.${field}`, value);
  };

  const handleSubProgramVenueChangeLocal = (index: number, value: string[]) => {
    setValue(`subPrograms.${index}.venueAddress`, value);
  };

  const handleSubProgramBannerUpload = (
    subProgramId: string,
    imageUrl: string,
  ) => {
    console.log(`Banner uploaded for subProgram ${subProgramId}:`, imageUrl);

    // Update the specific subProgram's bannerImageUrl in form data
    const subProgramIndex = fields.findIndex(
      (field) => field.id === subProgramId,
    );
    if (subProgramIndex !== -1) {
      setValue(`subPrograms.${subProgramIndex}.bannerImageUrl`, imageUrl);
    }

    // Call parent handler if provided
    if (onSubProgramBannerUpload) {
      onSubProgramBannerUpload(subProgramId, imageUrl);
    }
  };
  return (
    <div className={styles.formContainer}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
        <div className={styles.subSessionHeading}>Sub-Program Scheduler</div>
        {/* <Button
          buttonClassName={styles.programsDashboardAddButton}
          onClick={handleAddSubProgram}
          datatestid="create-infinipath"
          datatestidText="create-infinipath-text"
        >
          add sub-program
        </Button> */}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {fields.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              color: "text.secondary",
            }}
          >
            <Typography variant="body1">
              {isEditMode
                ? "No sessions available"
                : 'No sessions added yet. Click "Add Session" to create your first session.'}
            </Typography>
          </Box>
        )}

        {fields.map((field, index) => (
          <SubProgramCard
            key={field.id}
            control={control}
            index={index}
            watch={watch}
            setValue={setValue}
            errors={errors?.subPrograms?.[index]}
            onRemove={() => handleRemoveSubProgram(index)}
            venueOptions={venueOptions}
            onSubProgramBannerUpload={handleSubProgramBannerUpload}
            // Always use bannerImageUrl for the uploaded banner
            bannerImageUrl={field?.bannerImageUrl || null}
            isDateWithinProgramRange={isDateWithinProgramRange}
            currencyOptions={currencyOptions}
            programType={programType}
            startDate={startDate}
            endDate={endDate}
            trigger={trigger}
            onDateChange={handleSubProgramDateChange(index)}
            onFieldChange={(field: string, value: any) =>
              handleSubProgramFieldChange(index, field, value)
            }
            onVenueChange={(value: string[]) =>
              handleSubProgramVenueChangeLocal(index, value)
            }
            dateRange={
              getSubProgramDateRange
                ? getSubProgramDateRange(index.toString())
                : [null, null]
            }
            fieldlength={fields.length}
            subProgram={field as SubProgram}
            programTypeNameFromSource={programTypeNameFromSource}
          />
        ))}
      </Box>
      <DeleteSubProgramDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default SubProgramsSection;
